import { useMemo } from 'react';
import { ShoppingList as ShoppingListType, ShoppingListItem as ShoppingListItemType } from '@/types/api';
import { ShoppingListItem } from './ShoppingListItem';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle2,
  Plus,
  Share,
  Calendar,
  ShoppingBag,
  Package,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useShoppingListStore } from '@/stores/shoppingListStore';
import { formatDate } from '@/utils/dateUtils';

interface ShoppingListProps {
  shoppingList: ShoppingListType;
  onRefresh?: () => void;
  onShare?: () => void;
  isLoading?: boolean;
  className?: string;
}

// Aisle order for consistent sorting
const AISLE_ORDER = [
  'frutas',
  'verduras', 
  'carnes',
  'pescados',
  'lácteos',
  'despensa',
  'especias',
  'otros'
];

export function ShoppingList({
  shoppingList,
  onRefresh,
  onShare,
  isLoading = false,
  className
}: ShoppingListProps) {
  
  // Shopping list store
  const {
    checkedItems,
    customItems,
    toggleItemChecked,
    addCustomItem,
    removeCustomItem,
    toggleCustomItemChecked,
    getCheckedCount,
    getTotalCount,
    getCompletionPercentage,
    setShoppingList
  } = useShoppingListStore();

  // Update store when shopping list changes
  useMemo(() => {
    setShoppingList(shoppingList);
  }, [shoppingList, setShoppingList]);

  // Group items by aisle
  const itemsByAisle = useMemo(() => {
    const groups: Record<string, ShoppingListItemType[]> = {};
    
    // Group API items
    shoppingList.items.forEach((item) => {
      const aisle = item.aisle || 'otros';
      if (!groups[aisle]) {
        groups[aisle] = [];
      }
      groups[aisle].push(item);
    });

    // Group custom items
    customItems.forEach((customItem) => {
      const aisle = customItem.aisle || 'otros';
      if (!groups[aisle]) {
        groups[aisle] = [];
      }
      
      // Convert custom item to ShoppingListItem format
      groups[aisle].push({
        ingredient: customItem.ingredient,
        totalQuantity: customItem.quantity || null,
        unit: customItem.unit || null,
        aisle: customItem.aisle || null,
        recipes: ['(Agregado manualmente)']
      });
    });

    // Sort aisles by predefined order
    const sortedAisles: Record<string, ShoppingListItemType[]> = {};
    AISLE_ORDER.forEach((aisle) => {
      if (groups[aisle]) {
        sortedAisles[aisle] = groups[aisle].sort((a, b) => 
          a.ingredient.localeCompare(b.ingredient)
        );
      }
    });

    return sortedAisles;
  }, [shoppingList.items, customItems]);

  const totalItems = getTotalCount();
  const checkedCount = getCheckedCount();
  const completionPercentage = getCompletionPercentage();

  const handleAddCustomItem = () => {
    const ingredient = prompt('Nombre del ingrediente:');
    if (!ingredient?.trim()) return;

    const quantity = prompt('Cantidad (opcional):');
    const unit = prompt('Unidad (opcional):');
    
    addCustomItem({
      ingredient: ingredient.trim(),
      quantity: quantity ? parseFloat(quantity) : undefined,
      unit: unit?.trim(),
      aisle: 'otros'
    });
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShoppingBag className="h-5 w-5" />
                Lista de Compras
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="h-3 w-3 text-gray-500" />
                <span className="text-xs text-gray-500">
                  Semana del {formatDate(new Date(shoppingList.weekStart))}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Actions */}
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={isLoading}
                className="text-xs"
              >
                <RefreshCw className={cn("h-3 w-3 mr-1", isLoading && "animate-spin")} />
                Actualizar
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onShare}
                className="text-xs"
              >
                <Share className="h-3 w-3 mr-1" />
                Compartir
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Progreso de compras
              </span>
              <span className="font-medium">
                {checkedCount} / {totalItems} items
              </span>
            </div>
            <Progress 
              value={completionPercentage} 
              className="h-2"
            />
            <div className="text-center">
              <span className="text-xs text-gray-500">
                {completionPercentage}% completado
              </span>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Add Custom Item */}
      <Card>
        <CardContent className="p-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddCustomItem}
            className="w-full text-xs"
          >
            <Plus className="h-3 w-3 mr-2" />
            Agregar item personalizado
          </Button>
        </CardContent>
      </Card>

      {/* Shopping List by Aisle */}
      {Object.entries(itemsByAisle).map(([aisle, items]) => (
        <Card key={aisle}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4" />
              <span className="capitalize">{aisle}</span>
              <Badge variant="secondary" className="text-xs">
                {items.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pt-0">
            <div className="space-y-2">
              {items.map((item, index) => {
                const isCustom = item.recipes?.[0] === '(Agregado manualmente)';
                
                return (
                  <ShoppingListItem
                    key={`${item.ingredient}-${index}`}
                    item={item}
                    isChecked={
                      isCustom 
                        ? customItems.find(ci => ci.ingredient === item.ingredient)?.checked || false
                        : checkedItems[item.ingredient] || false
                    }
                    onToggleChecked={
                      isCustom 
                        ? (ingredient) => {
                            const customItem = customItems.find(ci => ci.ingredient === ingredient);
                            if (customItem) {
                              toggleCustomItemChecked(customItem.id);
                            }
                          }
                        : toggleItemChecked
                    }
                    onEdit={undefined}
                    onRemove={isCustom ? (item) => {
                      const customItem = customItems.find(ci => ci.ingredient === item.ingredient);
                      if (customItem) {
                        removeCustomItem(customItem.id);
                      }
                    } : undefined}
                    showRecipes={!isCustom}
                    isCustomItem={isCustom}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Empty State */}
      {Object.keys(itemsByAisle).length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingBag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Lista vacía
            </h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              Esta lista de compras está vacía. Agrega recetas a tu plan semanal para generar una lista automáticamente.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary Card */}
      {totalItems > 0 && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm">
              <div className="space-y-1">
                <div className="font-medium text-gray-900">
                  Resumen de la compra
                </div>
                <div className="text-gray-600">
                  {totalItems} ingredientes • {Object.keys(itemsByAisle).length} secciones
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="font-medium text-gray-900">
                    {completionPercentage}% listo
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {checkedCount} de {totalItems} items
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
